import { IUnitValue, IUserDashboardLabels } from 'shared/models';
import { observer } from 'mobx-react';
import { SortKey } from '../../index.tsx';
import { getStatusColor } from '../../../../../../utilities/getStatusColor.ts';
import { formatValueWithUnits } from '../../../../../../utilities/formatValueWithUnits.ts';
import { useDashboardDict } from '../../../../../../context/useDashboardDict.tsx';

import './styles.scss';
import classnames from 'classnames';
import { ICONS } from 'shared/constants';
import { UiIcon } from 'shared/uikit';

interface TableItem {
    key: string;
    employee?: string;
    store?: string;
    plan?: IUnitValue;
    fact?: IUnitValue;
    value?: IUnitValue;
}

type Props = {
    items?: TableItem[],
    onSort: (sortKey: SortKey) => void,
    labels?: IUserDashboardLabels;
    sortKey?: SortKey | null;
    sortOrder?: "asc" | "desc" | null;
    noTopRadius?: boolean;
};

export const PPDWidgetEfficiencyTable = observer(({
    items = [],
    onSort,
    sortKey = null,
    sortOrder = null,
    labels = {},
    noTopRadius
}: Props) => {

    const { units = {} } = useDashboardDict() || {};

    if (!items.length) {
        return null;
    }
    const renderHeader = (label: string, key?: SortKey) => (
        <th
            className={classnames(
                "ppd-widget-efficiency-table__head ppd-widget-efficiency-table__head--sortable",
                { "ppd-widget-efficiency-table__head--active": key && sortKey === key },
                { "ppd-widget-efficiency-table__head--no-top-radius": noTopRadius }
            )}
            onClick={() => key && onSort(key)}
        >
            <span className="ppd-widget-efficiency-table__head-text">
                {label}
                {key && (
                    <UiIcon
                        icon={ICONS.CHEVRON_DOWN}
                        size={12}
                        color={sortKey === key ? "#15171C" : "#787B83"}
                        className={classnames("ppd-widget-efficiency-table__sort-arrow", {
                            "ppd-widget-efficiency-table__sort-arrow--asc": sortKey === key && sortOrder === "asc",
                            "ppd-widget-efficiency-table__sort-arrow--desc": sortKey === key && sortOrder === "desc",
                        })}
                    />
                )}
            </span>
        </th>
    );

    return (
        <table className="ppd-widget-efficiency-table">
            <thead className="ppd-widget-efficiency-table__thead">
                <tr className="ppd-widget-efficiency-table__thead-row">
                    {renderHeader(labels?.employee_name || '-', "employee")}
                    {renderHeader(labels?.store || '-', "store")}
                    <th className="ppd-widget-efficiency-table__head">{labels?.plan || "-"}</th>
                    {renderHeader(labels?.result || "-", "value")}
                </tr>
            </thead>
            <tbody className="ppd-widget-efficiency-table__body">
                {items.map((row) => (
                    <tr className="ppd-widget-efficiency-table__row" key={row.key}>
                        <td className="ppd-widget-efficiency-table__cell">{row.employee || "—"}</td>
                        <td className="ppd-widget-efficiency-table__cell">{row.store || "—"}</td>
                        <td className="ppd-widget-efficiency-table__cell">
                            {formatValueWithUnits(row.plan ?? { amount: 0, unit_id: "currency" }, units) || "-"}
                        </td>
                        <td className="ppd-widget-efficiency-table__cell">
                            <span className="ppd-widget-efficiency-table__accent-value"  style={{ color: getStatusColor(row.value?.amount, '#2c2f35') }}>
                                {formatValueWithUnits(row.value ?? { amount: 0, unit_id: "percent" }, units) || "-"}
                            </span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
});
